// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Button } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import SearchOffIcon from '@mui/icons-material/SearchOff';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import EmptyStateCard from 'Components/EmptyStateCard';
import { PROJECTS_ROUTE } from 'Utils/routes';
import type { ProjectLoadFailure } from './projectLoadFailure';
import stylesDef from './styles';

export const PROJECT_LOAD_FAILURE_COPY: Record<ProjectLoadFailure, { title: string; description: string }> = {
  notFound: {
    title: 'Project not found',
    description: 'This project may have been deleted, or it belongs to another account.',
  },
  loadFailed: {
    title: 'This project could not be loaded',
    description: 'Something went wrong while loading it. Check your connection and try again.',
  },
};

interface ProjectNotFoundProps {
  failure: ProjectLoadFailure;
  onRetry: () => void;
}

// What a project page shows in place of its project: not found (deleted, or another account's), or not loaded
// this time, with Retry. Both lead back to Projects.
export const ProjectNotFound = ({ failure, onRetry }: ProjectNotFoundProps) => {
  const theme = useTheme();
  const styles = stylesDef(theme);
  const copy = PROJECT_LOAD_FAILURE_COPY[failure];

  return (
    <Box sx={styles.wrapper}>
      <Box sx={styles.content}>
        <EmptyStateCard
          icon={failure === 'notFound' ? <SearchOffIcon /> : <ErrorOutlineIcon />}
          title={copy.title}
          description={copy.description}
        />
        <Box sx={styles.actions}>
          {failure === 'loadFailed' && (
            <Button variant="contained" onClick={onRetry} sx={styles.actionButton}>
              Retry
            </Button>
          )}
          <Button
            variant={failure === 'loadFailed' ? 'outlined' : 'contained'}
            component={RouterLink}
            to={PROJECTS_ROUTE}
            sx={styles.actionButton}
          >
            Back to Projects
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default ProjectNotFound;
